import {
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useState } from "react";
import { useNavigate } from "react-router";
import { MaterialType } from "src/generated/graphqlTypes";
import { createDesignPackageUrl } from "src/RouteUrls";
import { pluralize, queryResult } from "src/utils";
import {
  CopyDesignPackageCardsModal_PlaceholderTliFragment,
  useCopyDesignPackageCardsModalQuery,
  useCopyDesignPackageCardsMutation,
} from "./CopyDesignPackageCardsModal.generated";

type CopyDesignPackageCardsModalProps = {
  designPackageId: string;
  designPackageVersionId: string;
  placeholderTli: CopyDesignPackageCardsModal_PlaceholderTliFragment;
};

export function CopyDesignPackageCardsModal(props: CopyDesignPackageCardsModalProps) {
  const tid = useTestIds({});
  const { designPackageId, designPackageVersionId, placeholderTli } = props;
  const { triggerNotice } = useSnackbar();
  const { closeModal } = useModal();
  const navigate = useNavigate();
  const [copyDpCards] = useCopyDesignPackageCardsMutation();
  const [selectedTlis, setSelectedTlis] = useState<string[]>([]);
  const materialVariantId = placeholderTli.materialVariant?.id;
  // Query for other existing placeholder TLIs that match the source TLI's material variant
  const query = useCopyDesignPackageCardsModalQuery({
    variables: {
      filter: {
        readyPlan: [designPackageId],
        materialType: [MaterialType.Placeholder],
        materialVariant: [materialVariantId].compact(),
      },
      designPackageVersionId,
    },
    skip: !materialVariantId,
  });

  return (
    <>
      <ModalHeader>Copy and Paste Selections</ModalHeader>
      <ModalBody>
        <div>Select other locations below to paste the selections from this location to.</div>
        {!materialVariantId && <div>The selected slot has no assigned material variant.</div>}
        {queryResult(query, ({ takeoffLineItemsPage }) => {
          const takeoffLineItems =
            takeoffLineItemsPage.items.filter((tli) => tli.active && !tli.personalizationOptionGroup) || [];
          return takeoffLineItems.isEmpty ? (
            <div>No Options Available</div>
          ) : (
            <div css={Css.my2.$}>
              <div css={Css.gray700.$}>Location</div>
              {takeoffLineItems
                .filter((tli) => tli.id !== placeholderTli.id)
                .sortBy((tli) => tli.location.displayLocationPath)
                .map((tli) => (
                  <div key={tli.id}>
                    <div css={Css.df.gap1.mt3.$} {...tid.matchingTli}>
                      <div css={Css.mya.$}>
                        <Checkbox
                          label={tli.name}
                          selected={selectedTlis.includes(tli.id)}
                          onChange={(val) =>
                            setSelectedTlis((prev) => (val ? [...prev, tli.id] : prev.filter((id) => id !== tli.id)))
                          }
                          checkboxOnly
                        />
                      </div>
                      <div>
                        {tli.name} - <span css={Css.smSb.$}>{tli.location.displayLocationPath}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          );
        })}
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          variant="primary"
          label="Paste Selections"
          onClick={async () => {
            // Create takeoff line items for selected locations
            const { data } = await copyDpCards({
              variables: {
                input: { sourcePlaceholderTliId: placeholderTli.id, targetPlaceholderTliIds: selectedTlis },
              },
              refetchQueries: ["DesignPackageSlots"],
            });
            const versionId = data!.copyDesignPackageCards.version.id;
            const onClick = () => navigate(`${createDesignPackageUrl(designPackageId, versionId)}`);
            triggerNotice({
              message: `Product selections have been copied to ${selectedTlis.length} other ${pluralize(selectedTlis.length, "location")}.`,
              icon: "success",
              ...(versionId !== designPackageVersionId ? { action: { label: "Go to draft", onClick } } : {}),
            });
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}
